import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch picture events (NIP-68) for a specific pubkey
 */
export function usePictures(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['pictures', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([
        {
          kinds: [20], // Picture events
          authors: [pubkey],
          limit: 50,
        }
      ], { signal });

      // Filter valid picture events
      return events
        .filter(validatePictureEvent)
        .sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Validator for picture events (NIP-68)
 */
export function validatePictureEvent(event: NostrEvent): boolean {
  if (event.kind !== 20) return false;

  // Must have at least one imeta tag
  const hasImeta = event.tags.some(([name]) => name === 'imeta');
  if (!hasImeta) return false;

  return true;
}

/**
 * Extract image URLs from a picture event
 */
export function extractImageUrls(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === 'imeta')
    .map(tag => {
      // Find the url in the imeta tag
      const urlPart = tag.find(part => part.startsWith('url '));
      return urlPart ? urlPart.replace('url ', '') : null;
    })
    .filter((url): url is string => url !== null);
}
